/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
  return new (P || (P = Promise))(function (resolve, reject) {
      function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
      function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
      function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
      step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var APEX_SLOT_TAGS;
(function (APEX_SLOT_TAGS) {
    APEX_SLOT_TAGS["SLOT"] = "s-slot";
    APEX_SLOT_TAGS["SLOT_ROOT"] = "s-root";
    APEX_SLOT_TAGS["FALLBACK_WRAPPER"] = "s-fallback-wrapper";
    APEX_SLOT_TAGS["ASSIGNED_WRAPPER"] = "s-assigned-wrapper";
})(APEX_SLOT_TAGS || (APEX_SLOT_TAGS = {}));
var APEX_SLOT_ATTRS;
(function (APEX_SLOT_ATTRS) {
    APEX_SLOT_ATTRS["SLOTTED"] = "data-apex-slotted";
})(APEX_SLOT_ATTRS || (APEX_SLOT_ATTRS = {}));

/* eslint-disable @typescript-eslint/no-explicit-any */
function ApexSlotableMixin(superclass, options) {
    const DEFAULT_SLOT_INTROSPECTION_OPTIONS = {
        ignoreComments: false,
        ignoreWhitespace: false
    };
    let slotTag = (options === null || options === void 0 ? void 0 : options.slotTag) || APEX_SLOT_TAGS.SLOT;
    let slotRootTag = (options === null || options === void 0 ? void 0 : options.slotRootTag) || APEX_SLOT_TAGS.SLOT_ROOT;
    if (options) {
        slotTag = options.slotTag || slotTag;
        slotRootTag = options.slotRootTag || slotRootTag;
    }
    class ApexSlot extends HTMLElement {
        constructor() {
            super();
            this.name = '';
            this._slotRendered = false;
            this._slotRenderAttempts = 0;
            this._maxSlotRenderAttempts = 10;
            this.name = this.getAttribute('name') || '';
        }
        connectedCallback() {
            this._slotUpdateCompleted = new Promise((resolve, reject) => {
                const id = setInterval(() => {
                    this._slotRenderAttempts++;
                    try {
                        if (this._slotRendered) {
                            resolve(undefined);
                        }
                        else if (this._slotRenderAttempts >= this._maxSlotRenderAttempts) {
                            throw new Error('Slot Rendering Timeout');
                        }
                    }
                    catch (e) {
                        reject(e);
                    }
                    finally {
                        clearInterval(id);
                    }
                }, 50);
            });
            this.sRoot = this.closest(slotRootTag);
            /**
             * We want the developer to remove the mixin as opposed to failing gracefully when it is
             * used on a component that does not contain any slots because
             * this component adds a lot of unnecessary code to components that don't need it
             * as well as trying to set up and remove observers.
             */
            if (!this.sRoot) {
                throw new Error('No slot in component! Remove mixin if not using slots!');
            }
            this.removeAttribute(APEX_SLOT_ATTRS.SLOTTED);
            // Observe the "Light DOM" of the component, to detect when new nodes are added and assign them to the <apex-slot> if necessary
            this.lightDomObserver = new MutationObserver(() => {
                this._slotRendered = false;
                this.updateAssignedContent();
            });
            this.lightDomObserver.observe(this.sRoot.parentElement, {
                childList: true
            });
            // Create assigned content and fallback content wrappers
            this.fallbackWrapper = this.fallbackWrapper || this.createFallbackWrapper();
            this.assignedWrapper = this.assignedWrapper || this.createAssignedWrapper();
            // Observe the assignedContentWrapper (so default content can be shown if all slotables are deleted)
            const assignedContentObserver = new MutationObserver(() => {
                this._slotRendered = false;
                this.updateEmptySlot(); // This is an observer on the actual <apex-slot>
                this.dispatchEvent(new CustomEvent('slotchange', {
                    bubbles: true
                }));
            });
            assignedContentObserver.observe(this.assignedWrapper, {
                childList: true
            });
            this.updateAssignedContent();
        }
        disconnectedCallback() {
            var _a;
            // we don't want to move any nested slot content
            if ((_a = this.assignedWrapper.parentElement) === null || _a === void 0 ? void 0 : _a.hasAttribute(APEX_SLOT_ATTRS.SLOTTED)) {
                return;
            }
            this.lightDomObserver.disconnect(); // don't let observers pile up
            const element = this.sRoot && this.sRoot.parentElement;
            const fragment = document.createDocumentFragment();
            Array.from(this.assignedWrapper.childNodes).forEach(child => {
                fragment.appendChild(child);
            });
            if (element) {
                element.appendChild(fragment);
            }
            this.sRoot = null;
        }
        createFallbackWrapper() {
            if (!customElements.get(APEX_SLOT_TAGS.FALLBACK_WRAPPER)) {
                const SFallbackWrapper = class extends HTMLElement {
                };
                customElements.define(APEX_SLOT_TAGS.FALLBACK_WRAPPER, SFallbackWrapper);
            }
            const fallbackNodes = Array.from(this.childNodes).filter(n => n.tagName === undefined || n.tagName.toLowerCase() !== APEX_SLOT_TAGS.ASSIGNED_WRAPPER);
            // This is only called once, get the contents of this <apex-slot> and wrap them in a span
            if (fallbackNodes.length === 0) {
                // there's no default content, don't create the wrapper
                return false;
            }
            else {
                const fallbackWrapper = document.createElement(APEX_SLOT_TAGS.FALLBACK_WRAPPER);
                fallbackNodes.forEach(node => {
                    fallbackWrapper.appendChild(node);
                });
                this.appendChild(fallbackWrapper); // Add the fallback span to the component;
                return fallbackWrapper;
            }
        }
        createAssignedWrapper() {
            if (!customElements.get(APEX_SLOT_TAGS.ASSIGNED_WRAPPER)) {
                const SAssignedWrapper = class extends HTMLElement {
                };
                customElements.define(APEX_SLOT_TAGS.ASSIGNED_WRAPPER, SAssignedWrapper);
            }
            const assignedWrapper = document.createElement(APEX_SLOT_TAGS.ASSIGNED_WRAPPER);
            this.appendChild(assignedWrapper); // Add the assigned span to the component;
            return assignedWrapper;
        }
        updateAssignedContent() {
            const lightDOM = this.sRoot.parentElement;
            const unplacedNodes = Array.from(lightDOM.childNodes).filter(node => {
                return node.parentNode === this.sRoot.parentElement && node !== this.sRoot; // return all nodes outside the <apex-root>, they haven't been moved yet
            });
            let content = [];
            if (this.name) {
                // Handle named slots
                content = unplacedNodes.filter(node => node.nodeType === Node.ELEMENT_NODE && node.getAttribute('slot') === this.name);
            }
            else {
                // Handle default slot content
                content = unplacedNodes.filter((n) => {
                    if (n.nodeType !== Node.ELEMENT_NODE) {
                        return n;
                    }
                    else if (typeof n.getAttribute === 'function' &&
                        !n.getAttribute('slot')) {
                        return n;
                    }
                    return undefined;
                });
            }
            if (content.length > 0) {
                // Some slotable content was found, remove default content
                const fragment = document.createDocumentFragment();
                content.forEach(node => {
                    // any child nodes that are slots, we need to make sure they don't potentially
                    // get moved out of place by disconnectedCallback on a parent slot unintentionally
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const allSlots = Array.from(node.querySelectorAll(slotTag));
                        if (node.nodeName === slotTag) {
                            node.setAttribute(APEX_SLOT_ATTRS.SLOTTED, '');
                        }
                        if (allSlots.length) {
                            allSlots.forEach(descendentNod => {
                                descendentNod.setAttribute(APEX_SLOT_ATTRS.SLOTTED, '');
                            });
                        }
                    }
                    fragment.appendChild(node);
                });
                this.assignedWrapper.appendChild(fragment);
                if (this.fallbackWrapper) {
                    this.fallbackWrapper.setAttribute('hidden', 'true');
                    this.assignedWrapper.removeAttribute('hidden'); // Do a visibility toggle so the mutationObserver will not be triggered and create a loop
                }
            }
            this._slotRendered = true;
        }
        updateEmptySlot() {
            if (this.fallbackWrapper && this.assignedWrapper.childNodes.length === 0) {
                this.fallbackWrapper.removeAttribute('hidden');
                this.assignedWrapper.setAttribute('hidden', 'true'); // Do a visibility toggle so the mutationObserver will not be triggered and create a loop
            }
            this._slotRendered = true;
        }
    }
    return class ClassInTransition extends superclass {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        constructor(..._args) {
            super();
            if (!customElements.get(slotRootTag)) {
                class ApexSlotRoot extends HTMLElement {
                }
                customElements.define(slotRootTag, ApexSlotRoot);
            }
            if (!customElements.get(slotTag)) {
                customElements.define(slotTag, ApexSlot);
            }
        }
        createRenderRoot() {
            // Wrap the entire rendered output in an <apex-root> element
            const existingSRoot = Array.from(this.childNodes).find(n => {
                var _a;
                return ((_a = n.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === slotRootTag;
            });
            if (existingSRoot !== undefined) {
                return existingSRoot;
            }
            else {
                return document.createElement(slotRootTag);
            }
        }
        firstUpdated() {
            // Ensure the contents are wrapped in the <apex-root> element
            if (!this.renderRoot.parentElement) {
                this.appendChild(this.renderRoot);
            }
            // @ts-ignore
            super.firstUpdated();
        }
        getUpdateComplete() {
            const _super = Object.create(null, {
                getUpdateComplete: { get: () => super.getUpdateComplete }
            });
            return __awaiter(this, void 0, void 0, function* () {
                yield _super.getUpdateComplete.call(this);
                const slotPromises = Array.from(this.querySelectorAll(slotTag)).map(
                // eslint-disable-next-line no-underscore-dangle
                s => s._slotUpdateCompleted);
                yield Promise.all(slotPromises);
                return true;
            });
        }
        /*
         * After the component has rendered, this method can be used to retrieve the content assigned to a slot
         */
        getAssignedSlotContent(slotName = 'default') {
            let slot;
            if (slotName === 'default') {
                slot = Array.from(this.querySelectorAll(slotTag))
                    .filter(n => n.getAttribute('name') === null)
                    .pop();
            }
            else {
                slot = this.querySelector(`${slotTag}[name='${slotName}']`);
            }
            if (!slot)
                return undefined; // Component hasn't rendered yet, no slot to query
            const slotContent = slot.querySelector(APEX_SLOT_TAGS.ASSIGNED_WRAPPER);
            if (slotContent === null || slotContent === void 0 ? void 0 : slotContent.childNodes) {
                return slotContent.childNodes;
            }
            return undefined;
        }
        /*
         * Before the component has rendered, this method can be used to retrieve child nodes that will be assigned to a slot
         */
        getSlotableContent(slotName = 'default', options = {}) {
            const sanitizedOptions = Object.assign(Object.assign({}, DEFAULT_SLOT_INTROSPECTION_OPTIONS), options);
            let slotableContent;
            if (slotName === 'default') {
                const isEmptyTextNode = (n) => {
                    return n.nodeType === Node.TEXT_NODE && (n.nodeValue == null || n.nodeValue.trim() === '');
                };
                // get all nodes outside apex-root that aren't assigned to another slot
                slotableContent = Array.from(this.childNodes).filter(n => {
                    // only Element nodes can have tagNames and attributes. if the node is something else, we can
                    // safely assume that it should be moved into the default slot.
                    if (sanitizedOptions.ignoreComments && n.nodeType === Node.COMMENT_NODE) {
                        return false;
                    }
                    else if (sanitizedOptions.ignoreWhitespace && isEmptyTextNode(n)) {
                        return false;
                    }
                    return (n.nodeType !== Node.ELEMENT_NODE ||
                        (n.tagName.toLowerCase() !== slotRootTag && n.getAttribute('slot') === null));
                });
            }
            else {
                slotableContent = Array.from(this.querySelectorAll(`[slot='${slotName}']`));
            }
            return slotableContent;
        }
        /*
         * After the component has rendered, this method can be used to determine if a slot has assigned content
         */
        hasAssignedSlotContent(slotName = 'default') {
            const assignedSlotContent = this.getAssignedSlotContent(slotName);
            if (typeof assignedSlotContent === 'undefined') {
                return false;
            }
            else {
                return assignedSlotContent.length > 0;
            }
        }
        /*
         * Before the component has rendered, this method can be used to determine if a slot will have content after the component renders
         */
        hasSlotableContent(slotName = 'default', options = {}) {
            const sanitizedOptions = Object.assign(Object.assign({}, DEFAULT_SLOT_INTROSPECTION_OPTIONS), { options });
            return this.getSlotableContent(slotName, sanitizedOptions).length > 0;
        }
    };
}

export { ApexSlotableMixin };
//# sourceMappingURL=utils.js.map
